// hooks/useSharedViews.ts
import { useState, useCallback } from 'react';
import { useFlashbar } from 'src/common/hooks/useFlashbar';
import { SharedViewStatus, UserViewPreferences } from '@amzn/controllershiphub-typescript-client';
import { getUserPreferences, saveUserPreferences } from 'src/utils/ControlSummaryUtils';
import { SharedView } from 'src/components/views/ShareView';
import { getSharedUserViews, updateSharedViewStatus } from 'src/utils/ShareViewUtils';

export const useSharedViews = (controlId: string) => {
  const [sharedViews, setSharedViews] = useState<SharedView[]>([]);
  const [isLoadingSharedViews, setIsLoadingSharedViews] = useState(false);
  const [savedFilterViews, setSavedFilterViews] = useState<Record<string, UserViewPreferences>>({});
  const [isUpdating, setIsUpdating] = useState(false);
  const { flashbarMessages, addFlashbarMessage, clearFlashbarMessages } = useFlashbar();

  // Load Shared Views
  const loadSharedViews = useCallback(async () => {
    if (!controlId) return;
    setIsLoadingSharedViews(true);
    try {
      const views = await getSharedUserViews(controlId);
      setSharedViews(views);
    } catch (error) {
      console.error('Error loading shared views:', error);
    } finally {
      setIsLoadingSharedViews(false);
    }
  }, [controlId]);

  // Load User Preferences
  const loadUserPreferences = useCallback(async () => {
    if (!controlId) return;
    try {
      const preferences = await getUserPreferences(controlId);
      setSavedFilterViews(preferences.views || {});
    } catch (error) {
      console.error('Error loading user preferences:', error);
    }
  }, [controlId]);

  // Handle Accept for a shared view
  const handleAcceptView = useCallback(
    async (view: SharedView) => {
      setIsUpdating(true);
      try {
        await updateSharedViewStatus({
          viewId: view.viewId!,
          controlId: controlId!,
          status: SharedViewStatus.ACCEPTED,
        });

        // Get existing user preferences
        const existingPrefs = await getUserPreferences(controlId!);

        // Create the new view entry with proper structure
        const newViewEntry: UserViewPreferences = {
          filterPreferences: view.viewPreference!.filterPreferences,
          columnPreferences: view.viewPreference!.columnPreferences,
          shared: true,
        };

        const viewname = view.viewName + '(' + view.sharedBy + ')';

        // Add the view to existing views with the same name
        const updatedViews = {
          ...(existingPrefs?.views || {}),
          [viewname]: newViewEntry,
        };

        // Update user preferences
        await saveUserPreferences(controlId!, updatedViews);

        // Update local state
        setSavedFilterViews(updatedViews);

        addFlashbarMessage({
          type: 'success',
          content: `Successfully accepted view: ${view.viewName}`,
        });
        await loadSharedViews();
        await loadUserPreferences();
      } catch (error) {
        console.error('Error accepting view:', error);
        addFlashbarMessage({
          type: 'error',
          content: `Failed to accept view 'Unknown error'`,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [controlId, addFlashbarMessage, loadSharedViews, loadUserPreferences]
  );

  // Handle Decline for a shared view
  const handleDeclineView = useCallback(
    async (view: SharedView) => {
      await updateSharedViewStatus({
        viewId: view.viewId!, // Use the existing viewId
        controlId: controlId!,
        status: SharedViewStatus.DECLINED,
      });

      addFlashbarMessage({
        type: 'success',
        content: `Successfully declined view: ${view.viewName}`,
      });
    },
    [controlId, addFlashbarMessage]
  );

  return {
    sharedViews,
    isLoadingSharedViews,
    savedFilterViews,
    isUpdating,
    loadSharedViews,
    loadUserPreferences,
    handleAcceptView,
    handleDeclineView,
  };
};
